// Types and path helpers for the creative-tools SEO pages. Deliberately free
// of locale imports so the client can use it without bundling 14 languages;
// the server-side loader with every locale lives in creativeToolsSeo.ts.
import {
  creativePresetPagePath,
  creativeToolIdFromSlug,
  creativeToolPagePath,
  creativeToolPresets,
  creativeToolRootPaths,
  creativeToolSlug,
  creativeToolSlugs,
  creativeTools,
  type CreativeCategory,
} from "./creativeTools";

export type CreativeSeoSection = {
  h2: string;
  body: string;
  /** Optional table under the paragraph (the ad page's price table). */
  table?: { head: string[]; rows: string[][] };
};

/** Numbered steps under the hero, also emitted as HowTo schema. */
export type CreativeSeoHowTo = {
  heading: string;
  description: string;
  steps: { name: string; text: string }[];
};

export type CreativeSeoPage = {
  title: string;
  description: string;
  keywords: string[];
  h1: string;
  intro: string;
  features: string[];
  faq: { q: string; a: string }[];
  /** Long-form blocks rendered between the features and the FAQ. */
  sections?: CreativeSeoSection[];
  howTo?: CreativeSeoHowTo;
  /** Heading over the page's example videos (CREATIVE_EXAMPLE_VIDEOS). */
  examplesHeading?: string;
};

export type CreativeSeoUi = {
  featuresHeading: string;
  faqHeading: string;
  ctaLabel: string;
  breadcrumbHome: string;
  breadcrumbHub: string;
  /** Heading of the link row between the ad pages (CREATIVE_AD_PAGE_KEYS). */
  adToolsHeading: string;
};

export type CreativeSeoLocale = {
  ui: CreativeSeoUi;
  pages: Record<string, CreativeSeoPage>;
};

/** Base path → content key for the hub and the three category pages. */
export const CREATIVE_HUB_PATHS: Record<string, "hub" | CreativeCategory> = {
  "/creative-tools": "hub",
  "/image-tools": "image",
  "/video-tools": "video",
  "/audio-tools": "audio",
};

export const CREATIVE_TOOL_PATH_PREFIX = "/creative-tools/";

/**
 * Tools that stay out of the index. Lip sync is a link card that hands off to
 * /ai-singing-video-generator, which owns that intent (its path 301s there,
 * see CREATIVE_TOOL_RETIRED_REDIRECTS).
 *
 * The two face swaps were here until Oct 25 2026 (off-topic for a music brand,
 * abuse-intent searches). Kitty asked for them in the index after Higgsfield's
 * face swap page showed about 71k organic visits a month; "ai face swap
 * video" is 9.3k US searches at KD 26 and "face swap video" 7k at KD 36
 * (Ahrefs). They went in with the consent line and the upload screen
 * (shared/creativeTools.ts CREATIVE_FACE_SWAP_TOOLS, server/faceSwapScreen.ts).
 */
export const CREATIVE_SEO_EXCLUDED_TOOLS = new Set([
  "lip-sync",
]);

export const creativeSeoToolIds: string[] = creativeTools
  .map((tool) => tool.id)
  .filter((id) => !CREATIVE_SEO_EXCLUDED_TOOLS.has(id));

/** Preset landings (own URL and copy, base tool underneath). */
export const creativeSeoPresetSlugs: string[] = Object.keys(creativeToolPresets);

/**
 * Every indexable creative-tools base path (hubs, tools, then presets). A
 * tool's path is its page path: /creative-tools/<slug>, or its root URL for
 * the tools in creativeToolRootPaths; a preset's is /creative-tools/<slug>,
 * or /<slug> for a root preset (creativePresetPagePath).
 */
export const CREATIVE_SEO_BASE_PATHS: string[] = [
  ...Object.keys(CREATIVE_HUB_PATHS),
  ...creativeSeoToolIds.map((id) => creativeToolPagePath(id)),
  ...creativeSeoPresetSlugs.map((slug) => creativePresetPagePath(slug)),
];

/** Root page path → tool id, for the tools served outside /creative-tools/. */
const ROOT_PATH_TOOL_IDS: Record<string, string> = Object.fromEntries(
  Object.entries(creativeToolRootPaths).map(([id, path]) => [path, id]),
);
/** Root page path → preset slug, for the presets served outside /creative-tools/. */
const ROOT_PATH_PRESETS: Record<string, string> = Object.fromEntries(
  creativeSeoPresetSlugs
    .filter((slug) => creativeToolPresets[slug].root)
    .map((slug) => [creativePresetPagePath(slug), slug]),
);

/** Content key for a base path, or null when the path is not a live tool. */
export function creativeSeoKeyForPath(basePath: string): string | null {
  const path = basePath.replace(/\/$/, "") || "/";
  const hubKey = CREATIVE_HUB_PATHS[path];
  if (hubKey) return hubKey;
  const rootToolId = ROOT_PATH_TOOL_IDS[path];
  if (rootToolId) {
    return creativeSeoToolIds.includes(rootToolId) ? rootToolId : null;
  }
  const rootPreset = ROOT_PATH_PRESETS[path];
  if (rootPreset) return rootPreset;
  if (!path.startsWith(CREATIVE_TOOL_PATH_PREFIX)) return null;
  const segment = path.slice(CREATIVE_TOOL_PATH_PREFIX.length);
  // A root preset is only a page at its root URL; its folder path 301s.
  if (creativeToolPresets[segment]) {
    return creativeToolPresets[segment].root ? null : segment;
  }
  // A re-slugged tool is only a page at its slug; its old id path 301s.
  if (creativeToolSlugs[segment]) return null;
  const toolId = creativeToolIdFromSlug(segment);
  // A root-path tool is only a page at its root URL; its folder path 301s.
  if (creativeToolRootPaths[toolId]) return null;
  return creativeSeoToolIds.includes(toolId) ? toolId : null;
}

/**
 * Old /creative-tools/ paths → the tool's page, for the server's 301 table:
 * a re-slugged tool's old id path, both folder paths (slug and id) of a
 * root-path tool, and the folder path of a root preset. Every entry points at
 * the final page, never at another redirect.
 */
export const CREATIVE_TOOL_SLUG_REDIRECTS: Record<string, string> =
  Object.fromEntries([
    ...Object.keys(creativeToolSlugs).map((id) => [
      `${CREATIVE_TOOL_PATH_PREFIX}${id}`,
      creativeToolPagePath(id),
    ]),
    ...Object.keys(creativeToolRootPaths).map((id) => [
      `${CREATIVE_TOOL_PATH_PREFIX}${creativeToolSlug(id)}`,
      creativeToolPagePath(id),
    ]),
    ...Object.values(ROOT_PATH_PRESETS).map((slug) => [
      `${CREATIVE_TOOL_PATH_PREFIX}${slug}`,
      creativePresetPagePath(slug),
    ]),
  ]);

/**
 * The AI video ad generator and its sibling ad pages (Oct 11 2026), linked to
 * each other in a "More ad tools" row on every one of them (the Zeely
 * "Discover more" pattern), by the server prerender (seo.ts) and the React
 * page (CreativeTools.tsx). Keys are creative SEO keys; each link's anchor
 * is the page's localized H1.
 */
export const CREATIVE_AD_PAGE_KEYS = [
  "song-promo-ad",
  "ai-ugc-video-generator",
  "ai-product-video-generator",
  "ai-commercial-generator",
  // Oct 25 2026: product shots for listings and for the ads themselves.
  "ai-product-photography",
] as const;

/** The other ad pages (key and page path) for an ad page key, else []. */
export function creativeAdPageLinks(key: string | null): { key: string; path: string }[] {
  if (!key || !(CREATIVE_AD_PAGE_KEYS as readonly string[]).includes(key)) return [];
  return CREATIVE_AD_PAGE_KEYS.filter((other) => other !== key).map((other) => ({
    key: other,
    path: creativeToolPresets[other]
      ? creativePresetPagePath(other)
      : creativeToolPagePath(other),
  }));
}

/**
 * Short root aliases (/image-to-video → /creative-tools/image-to-video) for
 * every indexable tool and preset under /creative-tools/, for the server's
 * 301 table. A typed, shared, or ad URL without the folder otherwise rendered
 * the SPA shell under the homepage title with noindex. The /creative-tools/
 * URL stays canonical and aliases never enter the sitemap; excluded tools get
 * no alias, and a root-path tool needs none (its root URL is the page).
 * Explicit root entries in the server table (e.g. /spotify-canvas-maker) win.
 */
export const CREATIVE_TOOL_SHORT_REDIRECTS: Record<string, string> =
  Object.fromEntries(
    CREATIVE_SEO_BASE_PATHS.filter((path) =>
      path.startsWith(CREATIVE_TOOL_PATH_PREFIX),
    ).map((path) => [`/${path.slice(CREATIVE_TOOL_PATH_PREFIX.length)}`, path]),
  );

/**
 * Retired tool ids that still get typed or crawled. These were presets in the
 * Sep 2026 catalog and were dropped when it was cut to the workflows a
 * musician needs (docs/creative-tools.md, "Scope"); the id metadata survives
 * only to display old jobs, so the URL rendered a "Tool not found" shell with
 * the homepage title. Each 301s to the live tool that does the same job (or,
 * for the link cards, to the product page they pointed at).
 */
const RETIRED_TOOL_TARGETS: Record<string, string> = {
  // Text-to-video prompt preset on an engine the catalog no longer runs.
  asmr: "/creative-tools/ai-video-generator",
  // Text-to-image prompt preset ("character reference sheet") on the image
  // generator's Standard engine. Its live successor is the AI Character
  // Generator preset over the same image generator (Oct 3 2026), a page, not
  // a redirect, so this stays one hop.
  "character-creator": "/creative-tools/ai-character-generator",
  // Sketch → finished image is the image-to-image tool with a prompt prefix.
  "draw-to-edit": "/creative-tools/image-to-image",
  // Both animated an uploaded drawing; image-to-video's own copy covers it.
  "draw-to-video": "/creative-tools/image-to-video",
  "sketch-to-video": "/creative-tools/image-to-video",
  // Two-image prompt presets on the same edit engine as image-to-image.
  "fashion-factory": "/creative-tools/image-to-image",
  "image-character-swap": "/creative-tools/image-to-image",
  moodboard: "/creative-tools/image-to-image",
  // A prompt preset on photo edit engines. Left out of the tool catalog; since
  // Oct 9 2026 the AI Hairstyle Changer preset over Image to Image does the
  // same job as a page (not a redirect), so this stays one hop.
  "hairstyle-changer": "/creative-tools/ai-hairstyle-changer",
  // Identical engine and output to the image upscaler.
  "image-enhancer": "/creative-tools/image-upscaler",
  // Link cards: never had a studio of their own.
  "lip-sync": "/ai-singing-video-generator",
  "lip-sync-video": "/ai-singing-video-generator",
  "lyrics-video": "/lyric-video-generator",
  "mixed-media": "/ai-music-video-generator",
  // Same ad-style presets on image-to-video as the AI video ad generator
  // (tool id song-promo-ad). Straight to its root page, never via a folder
  // path that redirects again.
  "marketing-studio": creativeToolPagePath("song-promo-ad"),
  // Relighting is a prompt edit; the image editor is the live prompt editor.
  relight: "/creative-tools/image-editor",
  "relight-lighting": "/creative-tools/image-editor",
};
export const CREATIVE_TOOL_RETIRED_REDIRECTS: Record<string, string> =
  Object.fromEntries(
    Object.entries(RETIRED_TOOL_TARGETS).map(([id, target]) => [
      `${CREATIVE_TOOL_PATH_PREFIX}${id}`,
      target,
    ]),
  );

/** A live tool page that is deliberately kept out of the index. */
export function isExcludedCreativeToolPath(basePath: string): boolean {
  const path = basePath.replace(/\/$/, "");
  if (!path.startsWith(CREATIVE_TOOL_PATH_PREFIX)) return false;
  const toolId = creativeToolIdFromSlug(
    path.slice(CREATIVE_TOOL_PATH_PREFIX.length),
  );
  return (
    CREATIVE_SEO_EXCLUDED_TOOLS.has(toolId) &&
    creativeTools.some((tool) => tool.id === toolId)
  );
}
